import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { useMe } from "@/auth/use-me";
import { BackButton } from "@/components/common/BackButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { AddPlaceholder } from "@/components/design/AddPlaceholder";
import { BoxHeader } from "@/components/design/BoxHeader";
import { SaveStatus } from "@/components/match/SaveStatus";
import { Button } from "@/components/design/Button";
import { SaveGroupNameDialog } from "@/components/save/SaveCollectionTemplate";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { useAgentStore } from "@/stores/agent-store";
import { downloadBlob } from "@/lib/file-download";
import { askToMarkdown, exportMarkdown, notebookFileName, notebookNotes, notebookSources } from "./notebook-markdown";
import { StudioExportDialog } from "./StudioExportDialog";
import { useStudioNotebooks, type StudioNotebook } from "./studio-notebooks";
import { StudioNotebookEditor } from "./StudioNotebookEditor";
import { StudioNotebookStream } from "./StudioNotebookStream";
import { StudioNotebookTitle } from "./StudioNotebookTitle";
import { Download, FilePen, FileText, FolderInput, UserPlus } from "lucide-react";
import type { StudioToolAction } from "./StudioToolPills";
import { useOrganizationalRecordAccess } from "@/pages/organizational-record/use-organizational-record-access";

export function StudioPage() {
  const me = useMe();
  return me.id ? <StudioWorkspace key={me.id} /> : <div className="p-6 text-smycles-body text-ink-muted"><FormattedMessage id="common.loading" defaultMessage="Loading…" /></div>;
}

function StudioWorkspace() {
  const intl = useIntl();
  const state = useStudioNotebooks();
  const [params, setParams] = useSearchParams();
  const notebookId = params.get("notebook");
  const pageRef = useRef<HTMLDivElement>(null);
  // Back on the list the page starts at the top; an open notebook starts at its end, which the stream does itself.
  useEffect(() => { if (!notebookId) pageRef.current?.scrollIntoView?.({ block: "start" }); }, [notebookId]);
  const book = state.notebooks.find((item) => item.id === notebookId);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const askInNotebook = useAgentStore((s) => s.askInNotebook);
  const pending = useAgentStore((s) => s.pending);
  // An open notebook rides every chat turn as data (@external-adr AGENT-SERVICE-0036-the-studio-proposes-records-the-user-decides);
  // the list view only says how many notebooks there are.
  useAssistantContext(book ? null : [{
    description: "Studio notebooks: unstructured research notes, not Person or Company records",
    value: `${state.notebooks.length} notebooks.`,
  }]);
  const setStudioNotebook = useAgentStore((s) => s.setStudioNotebook);
  // The assistant reads the notebook as numbered passages, so a proposal can be checked against the passage it
  // cites: the user's text, and the answers kept in it (a find from a search can become a proposal next).
  const sources = book ? notebookSources(book.text) : [];
  const openNotebookKey = book ? JSON.stringify([book.name, sources]) : "";
  useEffect(() => {
    if (!openNotebookKey) return;
    const [name, sources] = JSON.parse(openNotebookKey) as [string, { id: string; text: string }[]];
    setStudioNotebook({ name, notes: sources });
    return () => setStudioNotebook(null);
  }, [openNotebookKey, setStudioNotebook]);
  const canWritePerson = useHasPermission(Permission.WritePerson);
  const canCreateCompany = useHasPermission(Permission.CreateCompany);
  const canPropose = canWritePerson || canCreateCompany;
  const canFileRecords = useOrganizationalRecordAccess().canWrite;
  const askForFinal = (id: string) => askInNotebook(id, intl.formatMessage({ id: "studio.finalPrompt", defaultMessage: "Write a final version of this notebook as Markdown from everything in it: my notes, the documents I brought in and the answers kept here, duplicates merged, ordered by topic, nothing they say left out and nothing added. Reply with the Markdown only." }), "final");
  // The assistant's actions among the composer's pills: each a prompt in the notebook, offered only to someone
  // who may do what it leads to.
  const emptyHint = intl.formatMessage({ id: "studio.tools.emptyHint", defaultMessage: "First bring something into the notebook: a note, a document or an answer" });
  const aiActions = (id: string): StudioToolAction[] => [
    ...(canPropose ? [{
      id: "studio-to-records",
      label: intl.formatMessage({ id: "studio.toRecords", defaultMessage: "Turn into records" }),
      hint: intl.formatMessage({ id: "studio.toRecordsHint", defaultMessage: "Propose people and companies" }),
      icon: UserPlus,
      disabled: !sources.length,
      disabledHint: emptyHint,
      onSelect: () => askInNotebook(id, intl.formatMessage({ id: "studio.toRecordsPrompt", defaultMessage: "Propose records from this notebook." })),
    }] : []),
    ...(canFileRecords ? [{
      id: "studio-to-org-record",
      label: intl.formatMessage({ id: "studio.toOrgRecord", defaultMessage: "File in an organizational record" }),
      hint: intl.formatMessage({ id: "studio.toOrgRecordHint", defaultMessage: "As a note about a person" }),
      icon: FolderInput,
      disabled: !sources.length,
      disabledHint: emptyHint,
      onSelect: () => askInNotebook(id, intl.formatMessage({ id: "studio.toOrgRecordPrompt", defaultMessage: "File the content of this notebook as a note in an organizational record. Ask me which person if it is not clear." })),
    }] : []),
    {
      id: "studio-final",
      label: intl.formatMessage({ id: "studio.finalVersion", defaultMessage: "Write a final version" }),
      hint: intl.formatMessage({ id: "studio.finalVersionHint", defaultMessage: "Merged and ordered, as Markdown" }),
      icon: FileText,
      // From all of the notebook's material (Marvin, 2026-09-29): notes, documents and kept answers alike.
      disabled: !sources.length,
      disabledHint: emptyHint,
      onSelect: () => askForFinal(id),
    },
  ];
  const notebookActions: StudioToolAction[] = [
    {
      id: "studio-edit",
      label: intl.formatMessage({ id: "studio.editText", defaultMessage: "Edit text" }),
      icon: FilePen,
      disabled: !state.writable,
      onSelect: () => setEditing(true),
    },
    {
      id: "studio-export",
      label: intl.formatMessage({ id: "studio.export", defaultMessage: "Export" }),
      icon: Download,
      onSelect: () => setExporting(true),
    },
  ];
  const downloadExport = (notebook: StudioNotebook, withAnswers: boolean) => downloadBlob(
    new Blob([exportMarkdown(notebook, { withAnswers, proposalLabel: intl.formatMessage({ id: "studio.proposalLabel", defaultMessage: "Proposal" }), documentLabel: intl.formatMessage({ id: "studio.documentLabel", defaultMessage: "Document" }) })], { type: "text/markdown" }),
    notebookFileName(notebook.name),
  );
  const openNotebook = (id?: string) => {
    setEditing(false);
    const next = new URLSearchParams(params);
    if (id) next.set("notebook", id); else next.delete("notebook");
    setParams(next);
  };
  return <div ref={pageRef} id="studio-page" className="mt-8 max-w-[1600px] px-4 pb-12 @sm:px-6 @lg:px-10">
    <div className="mb-5"><BackButton /></div>
    <BoxHeader title={<FormattedMessage id="studio.title" defaultMessage="Studio" />} variant="hero" />
    <p className="smycles-field-meta text-ink"><FormattedMessage id="studio.description" defaultMessage="A place for loose thoughts, names and research finds." /></p>
    <p className="mt-2 smycles-field-meta text-ink-muted"><FormattedMessage id="studio.localNotice" defaultMessage="Notebooks are saved automatically and only you can see them. You can export any of them as Markdown at any time." /></p>
    {state.error ? <p role="alert" className="mt-4 text-smycles-body text-ink-accent"><FormattedMessage id="studio.storageError" defaultMessage="Your notebooks could not be saved or loaded. Please keep a copy of your text and try again." /></p> : null}
    {state.loading ? <p className="mt-8 text-smycles-body text-ink-muted"><FormattedMessage id="common.loading" defaultMessage="Loading…" /></p>
    : notebookId && !book ? <div className="mt-8 space-y-4">
      <p id="studio-not-found"><FormattedMessage id="studio.notFound" defaultMessage="This notebook does not exist (any more)." /></p>
      <Button onClick={() => openNotebook()}><FormattedMessage id="studio.notebooks" defaultMessage="Notebooks" /></Button>
    </div> : book ? <section className="mt-8">
      <Button id="studio-back" variant="ghost" size="compact" onClick={() => openNotebook()}><FormattedMessage id="studio.back" defaultMessage="← All notebooks" /></Button>
      <div className="mt-4 max-w-4xl">
        {/* The name is renamed in place, deleting stays apart; what else the notebook offers sits in the pills. */}
        <BoxHeader wrap title={<StudioNotebookTitle
          key={`${book.id}:${book.name}`}
          name={book.name}
          otherNames={state.notebooks.filter((item) => item.id !== book.id).map((item) => item.name)}
          editable={state.writable}
          onRename={(name) => state.renameNotebook(book.id, name)}
        />} right={
          // The match editor's save status (@adr SPARK-0048-studio-notebook-saves-like-the-match-editor@1), calm unless saving or failed.
          <div className="flex items-center gap-3">
            <SaveStatus saveState={state.saveState} lastSavedAt={state.lastSavedAt} onRetry={state.retry} />
            <Button id="studio-delete" variant="ghost" size="compact" disabled={!state.writable} onClick={() => setDeleting(true)}><FormattedMessage id="common.delete" defaultMessage="Delete" /></Button>
          </div>
        } />
      </div>
      <div className="mt-2 max-w-4xl">
        {editing
          ? <StudioNotebookEditor key={book.id} text={book.text} disabled={!state.writable} onSave={(text) => state.saveText(book.id, text)} onDone={() => setEditing(false)} />
          : <StudioNotebookStream key={book.id} book={book} writable={state.writable} onAppend={(text) => state.appendText(book.id, text)} onUndoAppend={(text) => state.undoAppend(book.id, text)} onKeepAsk={(ask) => state.appendText(book.id, askToMarkdown(ask))} onEditNote={(segment, expected, text) => state.editNote(book.id, segment, expected, text)} onEditCard={(askId, index, card) => state.editCard(book.id, askId, index, card)} aiActions={aiActions(book.id)} notebookActions={notebookActions} />}
      </div>
    </section> : <section className="mt-8">
      <BoxHeader title={<FormattedMessage id="studio.notebooks" defaultMessage="Notebooks" />} right={<span className="smycles-field-meta text-ink-muted">{state.notebooks.length}</span>} />
      <div id="studio-notebooks" className="flex flex-wrap items-start gap-4">
        {state.notebooks.map((notebook) => <button id={`studio-notebook-${notebook.id}`} key={notebook.id} type="button" onClick={() => openNotebook(notebook.id)}
          className="box flex h-32 w-72 max-w-full min-w-0 flex-col overflow-hidden bg-surface p-3 text-left transition hover:ring-ink/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40">
          <h2 className="line-clamp-2 break-words text-smycles-body font-medium leading-tight">{notebook.name}</h2>
          <p className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-smycles-label leading-snug text-ink-muted">{notebookNotes(notebook.text) || intl.formatMessage({ id: "studio.emptyPreview", defaultMessage: "Room for your next idea." })}</p>
          <div className="mt-auto flex flex-wrap items-center justify-end gap-2 pt-2 text-smycles-micro text-ink-muted">
            <time dateTime={new Date(notebook.updatedAt).toISOString()}>{intl.formatDate(notebook.updatedAt, { day: "numeric", month: "short" })}</time>
          </div>
        </button>)}
        <div className="h-32 w-72 max-w-full">
          <AddPlaceholder id="studio-create" fullWidth disabled={!state.writable} className="h-full" label={<FormattedMessage id="studio.newNotebook" defaultMessage="New notebook" />} onClick={() => setCreating(true)} />
        </div>
      </div>
    </section>}
    {creating ? <SaveGroupNameDialog open title={intl.formatMessage({ id: "studio.newNotebook", defaultMessage: "New notebook" })}
      initial=""
      existingNames={state.notebooks.map((item) => item.name)}
      placeholder={intl.formatMessage({ id: "studio.namePlaceholder", defaultMessage: "What are you collecting?" })}
      onClose={() => setCreating(false)}
      onSave={async (name) => {
        const id = await state.createNotebook(name);
        if (id) { setCreating(false); openNotebook(id); }
      }} /> : null}
    {book ? <StudioExportDialog
      open={exporting}
      finalDisabled={!sources.length || pending}
      onClose={() => setExporting(false)}
      onWithAnswers={() => { downloadExport(book, true); setExporting(false); }}
      onNotesOnly={() => { downloadExport(book, false); setExporting(false); }}
      onFinal={() => {
        askForFinal(book.id);
        setExporting(false);
      }}
    /> : null}
    <ConfirmDialog id="studio-confirm-delete" open={deleting} title={intl.formatMessage({ id: "studio.deleteNotebook", defaultMessage: "Delete notebook?" })}
      message={intl.formatMessage({ id: "studio.deleteNotebookMessage", defaultMessage: "The notebook is deleted with all its notes." })}
      onCancel={() => setDeleting(false)} onConfirm={async () => { if (book && await state.deleteNotebook(book.id)) { setDeleting(false); openNotebook(); } }} />
  </div>;
}
